import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["10:00", "12:00", "14:00", "16:00", "18:00"];

const inputCls =
  "w-full rounded-xl border border-[rgba(163,74,107,0.22)] bg-white px-4 py-3 text-sm text-cream placeholder:text-mutedlilac outline-none transition-colors duration-300 focus:border-[#A34A6B]/60";

export const BookingModal = ({ open, onClose, defaultName = "", defaultEmail = "" }) => {
  const [form, setForm] = useState({ first_name: "", email: "", date: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ first_name: defaultName, email: defaultEmail, date: "", time: "", notes: "" });
      setDone(false);
    }
  }, [open, defaultName, defaultEmail]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.date || !form.time) {
      toast.error("Please add your name, email and a preferred date and time.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2F1B36]/40 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-[rgba(163,74,107,0.2)] bg-[#FDFAF6] p-7 shadow-[0_40px_100px_rgba(47,27,54,0.25)] sm:p-9"
          >
            <button
              type="button"
              data-testid="booking-close-button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-[#A34A6B]/25 text-[#A34A6B] transition-colors duration-300 hover:bg-[#F9F3EE]"
            >
              <X className="h-4 w-4" />
            </button>

            {done ? (
              <div data-testid="booking-success" className="py-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#A34A6B]/30 bg-[#F3D9DC]">
                  <Check className="h-6 w-6 text-[#A34A6B]" />
                </span>
                <h2 className="mt-6 font-serif text-2xl text-cream">Your call request is in</h2>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-lilac">
                  Thank you{form.first_name ? `, ${form.first_name}` : ""}. Marie will confirm your discovery
                  call by email shortly.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-8 rounded-full bg-[#A34A6B] px-8 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-colors duration-300 hover:bg-[#8E3D5B]"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#A34A6B]">Discovery call</p>
                <h2 className="mt-3 font-serif text-2xl leading-snug text-cream">Book a call with Marie</h2>
                <p className="mt-2 text-sm text-lilac">Choose a preferred date and time, and Marie will confirm by email.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      data-testid="booking-first-name-input"
                      className={inputCls}
                      placeholder="First name"
                      value={form.first_name}
                      onChange={(e) => set("first_name", e.target.value)}
                      autoComplete="given-name"
                    />
                    <input
                      data-testid="booking-email-input"
                      type="email"
                      className={inputCls}
                      placeholder="Email address"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      data-testid="booking-date-input"
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      className={inputCls}
                      value={form.date}
                      onChange={(e) => set("date", e.target.value)}
                    />
                    <select
                      data-testid="booking-time-select"
                      className={`${inputCls} ${form.time ? "" : "text-mutedlilac"}`}
                      value={form.time}
                      onChange={(e) => set("time", e.target.value)}
                    >
                      <option value="" disabled>Preferred time</option>
                      {TIMES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    data-testid="booking-notes-input"
                    rows={2}
                    className={`${inputCls} resize-none`}
                    placeholder="Anything you'd like Marie to know? (optional)"
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                  />
                  <button
                    type="submit"
                    data-testid="booking-submit-button"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#A34A6B] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#8E3D5B] hover:shadow-[0_0_36px_rgba(163,74,107,0.3)] active:scale-[0.98] disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Request call
                  </button>
                  <p className="text-center text-[11px] leading-relaxed text-mutedlilac">
                    Your details are stored securely and never sold.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
